import React from "react";
import { SVGOverlay } from "react-map-gl";
import * as d3 from "d3";
import { beeswarm } from "d3-beeswarm";
import { Animate } from "react-move";
import { easeExpOut } from "d3-ease";
import styled from "styled-components";

import {
  CircleGraphsTitle,
  CirclesGraphContainer,
  SingleCircle,
  SingleCircleDetail,
  CircleAndDetail
} from "../StyledComponents";
import CircleGraph from "../graphs/CircleFillGraph";
import Axes from "./Axes";

import { devices, getDevice } from "../../services/getDevice";

const SubTitle = styled.text`
  fill: ${props => props.theme.textHighlight};
  font-size: 12px;
`;
const Title = styled.text`
  fill: ${props => props.theme.textHighlight};
  font-family: "dubai-bold";
  font-size: 19px;
`;

const PercentageLabel = styled.text`
  fill: ${props => props.theme.text};
  font-size: 9px;
  @media (max-width: 575px) {
    font-size: 10px;
  }

  @media (min-width: 1800px) {
    font-size: 16px;
  }
`;

const ForiegnObjectContainer = styled.div`
  padding: 0 25%;
  @media (max-width: 575px) {
    padding: 0 4%;
  }
`;

const BeeswarmOverlay = ({
  locations,
  view,
  onMapClick,
  serviceKPIsData,
  counts,
  totalCount
}) => {
  let vizWidth = window.innerWidth * 0.4,
    vizHeight = window.innerHeight * 0.45,
    circleRadius = 5;

  if (getDevice() === devices.mobile) {
    vizWidth = window.innerWidth * 0.85;
    vizHeight = window.innerHeight * 0.4;
  }
  if (getDevice() === devices.desktop) {
    circleRadius = 7;
  }

  let x = d3
    .scaleLinear()
    .rangeRound([0, vizWidth])
    .domain([0, 100]);

  let xpadded = d3
    .scaleLinear()
    .rangeRound([0, vizWidth - circleRadius * 2])
    .domain([0, 100]);

  let data = beeswarm()
    .data(locations)
    .distributeOn(function(d) {
      return xpadded(d.measures_happiness_meter);
    })
    .radius(circleRadius + 1)
    .orientation("horizontal")
    .side("symetric")
    .arrange();

  const renderMapView = (data, x, project) => {
    return data.map((location, i) => (
      <Animate
        key={i}
        start={() => ({
          x: project([
            parseFloat(location.datum.lng),
            parseFloat(location.datum.lat)
          ])[0],
          y: project([
            parseFloat(location.datum.lng),
            parseFloat(location.datum.lat)
          ])[1]
        })}
        enter={() => ({
          x: [
            location.x + x,
            project([
              parseFloat(location.datum.lng),
              parseFloat(location.datum.lat)
            ])[0]
          ],
          y: [
            location.y + vizHeight,
            project([
              parseFloat(location.datum.lng),
              parseFloat(location.datum.lat)
            ])[1]
          ],
          timing: { duration: 1000, ease: easeExpOut }
        })}
        update={() => ({
          x: [
            project([
              parseFloat(location.datum.lng),
              parseFloat(location.datum.lat)
            ])[0]
          ],
          y: [
            project([
              parseFloat(location.datum.lng),
              parseFloat(location.datum.lat)
            ])[1]
          ],
          timing: { duration: 1000, ease: easeExpOut }
        })}
      >
        {state => {
          return (
            <circle
              onClick={() => onMapClick(location)}
              r={circleRadius}
              fill="#E26A71"
              cx={state.x}
              cy={state.y}
            />
          );
        }}
      </Animate>
    ));
  };
  const renderVizView = (data, x, project) => {
    return data.map((location, i) => (
      <Animate
        key={i}
        start={() => ({
          x: [location.x + x + circleRadius],
          y: [location.y + vizHeight]
        })}
        enter={() => ({
          x: project([
            parseFloat(location.datum.lng),
            parseFloat(location.datum.lat)
          ])[0],
          y: project([
            parseFloat(location.datum.lng),
            parseFloat(location.datum.lat)
          ])[1],
          timing: { duration: 1000, ease: easeExpOut }
        })}
        update={() => ({
          x: [location.x + x + circleRadius],
          y: [location.y + vizHeight],
          timing: { duration: 1000, ease: easeExpOut }
        })}
      >
        {state => {
          return (
            <circle r={circleRadius} fill="#E26A71" cx={state.x} cy={state.y} />
          );
        }}
      </Animate>
    ));
  };

  const _redraw = ({ width, height, isDragging, project, unproject }) => {
    let center = window.innerWidth / 2 - vizWidth / 2,
      margin = { top: 0.1 * window.innerHeight, right: 0, bottom: 0, left: 0 };
    const isMobile = getDevice() === devices.mobile;
    if (isMobile) margin.top = 0.075 * window.innerHeight;

    if (view === "MAP") {
      return (
        <>
          <g className="nodes">{renderMapView(data, center, project)}</g>
        </>
      );
    } else {
      return (
        <>
          <g id="nodes" className="nodes">
            {renderVizView(data, center, project)}
          </g>

          <Axes
            width={vizWidth}
            scale={x}
            marginTop={margin.top + 150}
            x={center}
            y={vizHeight}
            counts={counts}
          />

          <use xlinkHref="#nodes" />
          <SubTitle
            x={window.innerWidth / 2}
            y={margin.top + 15}
            textAnchor="middle"
          >
            مجموع عدد مراكز الخدمة
          </SubTitle>
          <Title
            x={window.innerWidth / 2}
            y={margin.top + 50}
            textAnchor="middle"
          >
            {totalCount} مركز خدمة
          </Title>
          <PercentageLabel
            x={center - 5}
            y={vizHeight}
            textAnchor={isMobile ? "middle" : "end"}
            dominantBaseline="middle"
            transform={
              isMobile ? `rotate(90,${center - 10},${vizHeight})` : null
            }
          >
            0%
          </PercentageLabel>
          <PercentageLabel
            x={center + vizWidth + 5}
            y={vizHeight}
            dominantBaseline="middle"
            textAnchor={isMobile ? "middle" : "start"}
            transform={
              isMobile
                ? `rotate(90,${center + vizWidth + 10},${vizHeight})`
                : null
            }
          >
            100%
          </PercentageLabel>
          <foreignObject
            x={0}
            y={isMobile ? vizHeight + 50 : vizHeight + 100}
            height="100%"
            width="100%"
          >
            <ForiegnObjectContainer>
              <CircleGraphsTitle>متوسط أداء مؤشرات الخدمات</CircleGraphsTitle>
              <CirclesGraphContainer>
                {serviceKPIsData.map((item, i) => (
                  <CircleAndDetail key={`service-circle-${i}`}>
                    <SingleCircle>
                      <CircleGraph
                        secondary
                        key={i}
                        value={item.value}
                        title={item.name}
                      />
                    </SingleCircle>
                    <SingleCircleDetail>
                      {item.name}
                      <br />
                      {item.detail}
                    </SingleCircleDetail>
                  </CircleAndDetail>
                ))}
              </CirclesGraphContainer>
            </ForiegnObjectContainer>
          </foreignObject>
        </>
      );
    }
  };
  return <SVGOverlay redraw={_redraw} />;
};

export default BeeswarmOverlay;
